/**
 * Start ⇄ rule reconciliation: "satisfied, or refused — never reshaped".
 *
 * The contract (docs/calendar-sync.md, Microsoft Graph section): when a
 * recurring series' start moves, either the rule already produces the new
 * start (nothing to do), or the move is the unambiguous single-day WEEKLY
 * drag (rewrite the one BYDAY token), or the move is REFUSED and the user
 * edits the repeat rule. Rewriting an arbitrary RRULE to match a new start
 * was built, generalised, and reverted: every version silently DELETED
 * occurrences behind a success toast. Losing a move is bad; losing the
 * series is worse.
 *
 * Evaluation design (the reason this lives here and not in five hand-rolled
 * copies): pattern membership is answered by the RRULE LIBRARY, never by
 * hand-written BY* predicates — WKST, ordinal BYDAY, BYSETPOS's
 * expanded-set indexing, negative values and expand/limit interactions are
 * exactly what kept being re-derived wrong. Mechanics:
 *
 *  - The membership rule is anchored AT THE CANDIDATE, so implicit defaults
 *    (a WEEKLY rule with no BYDAY inherits its anchor's weekday; MONTHLY
 *    inherits the day-of-month) materialise from the candidate — matching
 *    what the rule will mean once the move is saved. Membership =
 *    "anchored here, is the candidate the rule's own first occurrence".
 *  - INTERVAL phase is anchor-relative, so it is checked SEPARATELY with
 *    plain calendar arithmetic against the previous start (the only hand
 *    arithmetic permitted here — day/week/month/year distances, no BY*
 *    semantics).
 *  - COUNT and UNTIL are stripped from the membership rule: UNTIL is
 *    resolved by the shared `untilInstantMs` policy beforehand, and a moved
 *    series re-anchors, making its new start occurrence #1 — trivially
 *    within any COUNT.
 *  - Every library call carries a synthetic two-year lookahead UNTIL and
 *    runs under the patched iterator's advance budget, so a hostile or
 *    contradictory rule refuses (`unsupported`) instead of blocking the
 *    event loop. An unevaluable rule REFUSES rather than sailing through —
 *    Microsoft accepts a contradicting move with 200 and silently discards
 *    it, so "can't tell" must never become "allowed".
 */

import { RRule, Frequency, type Options } from 'rrule';
import { parseRecurrenceText, rruleValueParts } from './text';
import { untilInstantMs } from './until';
import { canonicalRrule } from './compare';
import { hasFixedOffset, toFloating } from './zone';

export type RuleAdjustment =
  | { kind: 'unchanged' }
  | { kind: 'rewritten'; rrule: string }
  | { kind: 'unsupported'; reason: string };

export interface RuleForNewStartOptions {
  /** The series' effective anchor zone — resolve as
   *  `rruleTimezone ?? timezone` (callers pass 'UTC' for all-day). */
  zone: string;
  /** The start before the edit. Absent ⇒ nothing moved ⇒ `unchanged`. */
  previousStartIso?: string | null;
  isAllDay?: boolean;
  /** The previous start is read in ITS OWN representation — pass the
   *  pre-edit all-day flag so an all-day toggle doesn't misread it. */
  previousIsAllDay?: boolean;
}

// User-facing refusal copy — kept byte-identical to the client strings the
// tests pinned, so the consolidation is not a wording change.
const REASON_UNREADABLE = 'The new start date could not be read.';
const REASON_PAST_UNTIL =
  'The new date is after this series ends, so no occurrences would remain.';
const REASON_MULTI_WEEKDAY =
  'This series repeats on several weekdays. Edit the repeat rule to move the whole series.';
const REASON_MONTHLY_YEARLY =
  'This series repeats on a monthly or yearly pattern. Edit the repeat rule to move the whole series.';
const REASON_INTERVAL =
  'This series repeats on an interval the new date does not fall on. Edit the repeat rule to move the whole series.';
const REASON_UNSUPPORTED_RULE =
  'This repeat rule could not be safely adjusted. Edit the repeat rule to move the whole series.';

const DATE_LEVEL_FREQ = new Set<Frequency>([
  RRule.DAILY,
  RRule.WEEKLY,
  RRule.MONTHLY,
  RRule.YEARLY,
]);

/** Synthetic lookahead cap for membership scans (bounded by the patched
 *  iterator's UNTIL check). Two years covers any legitimate first-emission
 *  distance for DAILY..YEARLY at the validated INTERVAL cap. */
const MEMBERSHIP_LOOKAHEAD_MS = 2 * 366 * 24 * 60 * 60 * 1000;

export type SatisfactionResult =
  | { ok: true; satisfied: boolean }
  | { ok: false; refusedReason: string };

interface ParsedRule {
  options: Partial<Options>;
  parts: Array<[string, string]>;
  hasEnvelopeConstructs: boolean;
}

function parseRule(recurrenceText: string): ParsedRule | { refused: string } {
  const parsed = parseRecurrenceText(recurrenceText);
  if (!parsed) return { refused: REASON_UNSUPPORTED_RULE };
  const parts = rruleValueParts(parsed.rrule);
  if (!parts) return { refused: REASON_UNSUPPORTED_RULE };
  let options: Partial<Options>;
  try {
    options = RRule.parseString(parsed.rrule);
  } catch {
    return { refused: REASON_UNSUPPORTED_RULE };
  }
  if (options.freq === undefined || !DATE_LEVEL_FREQ.has(options.freq)) {
    return { refused: REASON_UNSUPPORTED_RULE };
  }
  return {
    options,
    parts,
    hasEnvelopeConstructs: parsed.hasRdate || parsed.hasExrule,
  };
}

/** The candidate in evaluation space: floating wall-clock for zoned series,
 *  the raw instant for UTC/fixed-offset/floating ones. */
function evaluationDate(instantIso: string, zone: string): Date | null {
  const instant = new Date(instantIso);
  if (Number.isNaN(instant.getTime())) return null;
  return hasFixedOffset(zone) ? instant : toFloating(instant, zone);
}

/**
 * Pattern membership: anchored at `candidate`, does the rule emit
 * `candidate` as its own first occurrence? Anchor-relative parts (INTERVAL
 * phase) are NOT checked here — see `intervalPhaseAligned`.
 */
function patternMembership(options: Partial<Options>, candidate: Date): SatisfactionResult {
  let rule: RRule;
  try {
    rule = new RRule({
      ...options,
      dtstart: candidate,
      count: null,
      until: new Date(candidate.getTime() + MEMBERSHIP_LOOKAHEAD_MS),
    });
  } catch {
    return { ok: false, refusedReason: REASON_UNSUPPORTED_RULE };
  }
  try {
    const hits = rule.between(candidate, candidate, true);
    return { ok: true, satisfied: hits.length > 0 };
  } catch (err) {
    if (err instanceof Error && err.message.includes('RRULE_ITERATION_LIMIT')) {
      return { ok: false, refusedReason: REASON_UNSUPPORTED_RULE };
    }
    throw err;
  }
}

// ─── Interval phase ──────────────────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;

function utcDayOrdinal(d: Date): number {
  return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / DAY_MS);
}

/** rrule weekday numbers: MO=0 … SU=6 (RRule.MO.weekday === 0). */
function rruleWeekday(d: Date): number {
  return (d.getUTCDay() + 6) % 7;
}

/**
 * Whether `candidate` falls on the same INTERVAL cadence as `anchor`.
 * Definable only relative to an existing anchor, so this is evaluated
 * against the PREVIOUS start during reconciliation. Pure calendar
 * arithmetic in floating space; week boundaries follow WKST (RFC 5545:
 * WEEKLY+INTERVAL counts weeks aligned to the week start, default MO).
 */
export function intervalPhaseAligned(
  freq: Frequency,
  interval: number,
  wkst: number,
  anchor: Date,
  candidate: Date,
): boolean {
  if (interval <= 1) return true;
  switch (freq) {
    case RRule.DAILY: {
      const delta = utcDayOrdinal(candidate) - utcDayOrdinal(anchor);
      return ((delta % interval) + interval) % interval === 0;
    }
    case RRule.WEEKLY: {
      const weekStartOrdinal = (d: Date): number => {
        const ord = utcDayOrdinal(d);
        const offset = (((rruleWeekday(d) - wkst) % 7) + 7) % 7;
        return ord - offset;
      };
      const weeks = (weekStartOrdinal(candidate) - weekStartOrdinal(anchor)) / 7;
      return ((weeks % interval) + interval) % interval === 0;
    }
    case RRule.MONTHLY: {
      const months =
        (candidate.getUTCFullYear() - anchor.getUTCFullYear()) * 12 +
        (candidate.getUTCMonth() - anchor.getUTCMonth());
      return ((months % interval) + interval) % interval === 0;
    }
    case RRule.YEARLY: {
      const years = candidate.getUTCFullYear() - anchor.getUTCFullYear();
      return ((years % interval) + interval) % interval === 0;
    }
    default:
      return false;
  }
}

/**
 * Bounded, library-backed satisfaction: pattern membership only (see the
 * module doc for why INTERVAL phase is separate). Refusals (`ok: false`)
 * mean the rule could not be safely evaluated — callers must treat that as
 * "not allowed", never as "fine".
 */
export function startSatisfiesRule(
  recurrenceText: string,
  startIso: string,
  zone: string,
): SatisfactionResult {
  const parsed = parseRule(recurrenceText);
  if ('refused' in parsed) return { ok: false, refusedReason: parsed.refused };
  if (parsed.hasEnvelopeConstructs) {
    // RDATE adds occurrences and EXRULE removes them by a second rule —
    // membership against the RRULE alone would answer the wrong question.
    return { ok: false, refusedReason: REASON_UNSUPPORTED_RULE };
  }
  const candidate = evaluationDate(startIso, zone);
  if (!candidate) return { ok: false, refusedReason: REASON_UNREADABLE };
  return patternMembership(parsed.options, candidate);
}

// ─── ruleForNewStart ─────────────────────────────────────────────────────────

const PLAIN_BYDAY = /^(MO|TU|WE|TH|FR|SA|SU)$/i;
const WEEKDAY_TOKENS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;

/**
 * Reconciles a recurrence rule with a moved start. Decision table
 * (docs/calendar-sync.md; the notes' §2 contract — preserved exactly, with
 * one deliberate flip: an UNEVALUABLE rule now refuses instead of sailing
 * through to Microsoft's silent discard):
 *
 *  | situation                                          | outcome       |
 *  |----------------------------------------------------|---------------|
 *  | no rule                                            | unchanged     |
 *  | unreadable new start                               | unsupported   |
 *  | no previous start (create / template)              | unchanged     |
 *  | past the series' own UNTIL                         | unsupported   |
 *  | rule unevaluable (parse/sub-daily/RDATE/EXRULE/…)  | unsupported   |
 *  | rule already produces the new start (incl. phase)  | unchanged     |
 *  | the OLD start did not satisfy the rule either      | unchanged     |
 *  | single-day WEEKLY moved off its day, phase intact  | rewritten     |
 *  | anything else                                      | unsupported   |
 *
 * Callers handle `unsupported` by refusing the save.
 */
export function ruleForNewStart(
  recurrenceText: string | null | undefined,
  startIso: string,
  options: RuleForNewStartOptions,
): RuleAdjustment {
  if (!recurrenceText) return { kind: 'unchanged' };

  const zone = options.zone;
  const candidate = evaluationDate(startIso, options.isAllDay ? 'UTC' : zone);
  if (!candidate) return { kind: 'unsupported', reason: REASON_UNREADABLE };

  // No move ⇒ nothing adjusted and nothing refused (create / template path).
  if (!options.previousStartIso) return { kind: 'unchanged' };

  // An UNMOVED start reconciles trivially — before any parsing or refusal.
  // A stored rule this evaluator cannot model (sub-daily from another CalDAV
  // client, RDATE, …) must not block a save that doesn't move the series;
  // the server's in-transaction check early-returns on no-move for the same
  // reason, and the two must agree.
  const previousInstant = new Date(options.previousStartIso).getTime();
  if (!Number.isNaN(previousInstant) && new Date(startIso).getTime() === previousInstant) {
    return { kind: 'unchanged' };
  }

  const parsed = parseRule(recurrenceText);
  if ('refused' in parsed) return { kind: 'unsupported', reason: parsed.refused };
  if (parsed.hasEnvelopeConstructs) {
    return { kind: 'unsupported', reason: REASON_UNSUPPORTED_RULE };
  }

  // Past the series' own end: the move would leave no occurrences at all.
  const untilMs = untilInstantMs(recurrenceText, options.isAllDay ? 'UTC' : zone);
  const candidateInstant = new Date(startIso).getTime();
  if (untilMs !== null && candidateInstant > untilMs) {
    return { kind: 'unsupported', reason: REASON_PAST_UNTIL };
  }

  const freq = parsed.options.freq as Frequency;
  const interval = parsed.options.interval ?? 1;
  const wkst =
    typeof parsed.options.wkst === 'number'
      ? parsed.options.wkst
      : (parsed.options.wkst?.weekday ?? 0);

  const previousZone = (options.previousIsAllDay ?? options.isAllDay) ? 'UTC' : zone;
  const previous = evaluationDate(options.previousStartIso, previousZone);

  const membership = patternMembership(parsed.options, candidate);
  if (!membership.ok) return { kind: 'unsupported', reason: membership.refusedReason };
  const phaseAligned = previous
    ? intervalPhaseAligned(freq, interval, wkst, previous, candidate)
    : true;

  if (membership.satisfied && phaseAligned) return { kind: 'unchanged' };

  // Not this edit's doing: when the OLD start did not satisfy the rule
  // either, the mismatch predates the move — leave the rule alone.
  if (previous) {
    const prevMembership = patternMembership(parsed.options, previous);
    if (!prevMembership.ok) {
      return { kind: 'unsupported', reason: prevMembership.refusedReason };
    }
    if (!prevMembership.satisfied) return { kind: 'unchanged' };
  }

  // Pattern satisfied but the INTERVAL cadence missed: no BYDAY splice can
  // fix a phase mismatch (the bi-weekly off-week drag).
  if (membership.satisfied && !phaseAligned) {
    return { kind: 'unsupported', reason: REASON_INTERVAL };
  }

  if (freq === RRule.WEEKLY) {
    const byDayParts = parsed.parts.filter(([key]) => key === 'BYDAY');
    const tokens = byDayParts.flatMap(([, v]) => v.split(',').map((t) => t.trim()));
    if (byDayParts.length === 1 && tokens.length === 1 && PLAIN_BYDAY.test(tokens[0])) {
      if (!phaseAligned) return { kind: 'unsupported', reason: REASON_INTERVAL };
      const rewritten = spliceWeeklyByDay(recurrenceText, WEEKDAY_TOKENS[rruleWeekday(candidate)]);
      if (rewritten === null) {
        return { kind: 'unsupported', reason: REASON_UNSUPPORTED_RULE };
      }
      // Property invariant, verified per call: the rewritten rule must
      // produce the new start ("losing a move is bad; losing the series is
      // worse" — a rewrite that empties the series must never be emitted).
      const check = startSatisfiesRule(rewritten, startIso, options.isAllDay ? 'UTC' : zone);
      if (!check.ok || !check.satisfied) {
        return { kind: 'unsupported', reason: REASON_UNSUPPORTED_RULE };
      }
      return { kind: 'rewritten', rrule: rewritten };
    }
    if (tokens.length > 1) {
      return { kind: 'unsupported', reason: REASON_MULTI_WEEKDAY };
    }
    return { kind: 'unsupported', reason: REASON_INTERVAL };
  }

  if (freq === RRule.MONTHLY || freq === RRule.YEARLY) {
    return { kind: 'unsupported', reason: REASON_MONTHLY_YEARLY };
  }

  return { kind: 'unsupported', reason: REASON_INTERVAL };
}

/**
 * Replaces the single plain BYDAY token in place — the rest of the value,
 * and every envelope line of a multi-line component, stays byte-stable.
 */
function spliceWeeklyByDay(recurrenceText: string, newDay: string): string | null {
  const parsed = parseRecurrenceText(recurrenceText);
  if (!parsed) return null;
  const parts = parsed.rrule.split(';');
  const idx = parts.findIndex((p) => p.trim().toUpperCase().startsWith('BYDAY='));
  if (idx === -1) return null;
  parts[idx] = `BYDAY=${newDay}`;
  const rewrittenValue = parts.join(';');
  if (!parsed.isMultiLine) {
    // A legacy `RRULE:` prefix is stripped on the way through — the vault
    // column stores the bare VALUE, and this is the one place the rule is
    // being rewritten anyway (pinned by the ported contract tests).
    return rewrittenValue;
  }
  return [...parsed.linesBeforeRrule, `RRULE:${rewrittenValue}`, ...parsed.linesAfterRrule].join(
    '\n',
  );
}

// ─── patchReschedulesSeries ──────────────────────────────────────────────────

export interface ReschedulePatchShape {
  startAt?: string | Date;
  endAt?: string | Date;
  rrule?: string | null;
  rruleTimezone?: string | null;
  isAllDay?: boolean;
  timezone?: string;
}

export interface RescheduleExistingShape {
  startAt: string | Date;
  endAt: string | Date;
  rrule: string | null;
  rruleTimezone: string | null;
  isAllDay: boolean;
  timezone: string | null;
}

function instantEqual(a: string | Date, b: string | Date): boolean {
  return new Date(a).getTime() === new Date(b).getTime();
}

/**
 * True when the patch changes the series' TIMING: startAt, endAt, rrule
 * (canonically compared), rruleTimezone, or isAllDay differ from the
 * existing row. `timezone` counts only when it is the row's EFFECTIVE
 * expansion anchor (a recurring row with `rruleTimezone` null) — otherwise
 * it is display metadata and never timing.
 *
 * PATCH semantics: an absent field changes nothing. This predicate drives
 * the warned-reset policy, so it must never fire for content-only edits
 * (title, description, color…).
 */
export function patchReschedulesSeries(
  patch: ReschedulePatchShape,
  existing: RescheduleExistingShape,
): boolean {
  if (patch.startAt !== undefined && !instantEqual(patch.startAt, existing.startAt)) return true;
  if (patch.endAt !== undefined && !instantEqual(patch.endAt, existing.endAt)) return true;
  if (patch.isAllDay !== undefined && patch.isAllDay !== existing.isAllDay) return true;
  if (
    patch.rrule !== undefined &&
    canonicalRrule(patch.rrule ?? '') !== canonicalRrule(existing.rrule ?? '')
  ) {
    return true;
  }
  if (
    patch.rruleTimezone !== undefined &&
    (patch.rruleTimezone ?? null) !== (existing.rruleTimezone ?? null)
  ) {
    return true;
  }
  if (
    patch.timezone !== undefined &&
    existing.rrule !== null &&
    existing.rruleTimezone === null &&
    patch.timezone !== (existing.timezone ?? null)
  ) {
    return true;
  }
  return false;
}
